import { AnyIcon } from "sharedUI/ui/views/glide-icon/glide-icon";

export type ButtonType = "primary" | "secondary" | "tertiary" | "minimal";
export type ButtonVariants = "default" | "accent" | "gradient" | "inverse" | "success" | "warning" | "danger";
export type IconType = "default" | "iconLeading" | "iconTrailing" | "segmented" | "iconOnly";
export type ButtonSizes = "xsm" | "sm" | "md" | "lg" | "xlg";
export type SplitType = "left" | "right" | "middle";

export type ButtonStyleProps = {
    readonly variant: ButtonVariants;
    readonly buttonType: ButtonType;
    readonly size: ButtonSizes;
    readonly label: string;

    readonly onClick?: () => void;
    readonly split?: SplitType;
    readonly iconType?: IconType;
    readonly href?: string;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly isFullWidth?: boolean;
    readonly isProcessing?: boolean;
    readonly icon?: AnyIcon;
};

export const InvalidButtonStyles = ["primaryaccent", "secondaryinverse", "tertiaryinverse", "minimalinverse"];

export const IconSize = {
    xsm: 16,
    sm: 20,
    md: 20,
    lg: 24,
    xlg: 24,
};

export const SegmentedIconSize = {
    xsm: 12,
    sm: 16,
    md: 16,
    lg: 20,
    xlg: 24,
};

export const ButtonSizeMap = {
    xsm: 24,
    sm: 28,
    md: 32,
    lg: 36,
    xlg: 40,
};

export const getIconSize = (size: ButtonSizes, iconType?: IconType, split?: SplitType) => {
    if (split && iconType === "iconOnly") {
        return SegmentedIconSize[size];
    }
    return IconSize[size];
};

export type ButtonStyles =
    | "primarydefault"
    | "primarygradient"
    | "primaryinverse"
    | "primarysuccess"
    | "primarywarning"
    | "primarydanger"
    | "secondarydefault"
    | "secondaryaccent"
    | "secondarygradient"
    | "secondarysuccess"
    | "secondarywarning"
    | "secondarydanger"
    | "tertiarydefault"
    | "tertiaryaccent"
    | "tertiarygradient"
    | "tertiarysuccess"
    | "tertiarywarning"
    | "tertiarydanger"
    | "minimaldefault"
    | "minimalaccent"
    | "minimalgradient"
    | "minimalsuccess"
    | "minimalwarning"
    | "minimaldanger";

export const backgroundColorMapping: { [key: string]: string } = {
    primarydefault: "accentPrimary",
    primarygradient: "brandGradient",
    primaryinverse: "n900",
    primarysuccess: "g400",
    primarywarning: "y400",
    primarydanger: "r400",
    secondarydefault: "n200A",
    secondaryaccent: "b100",
    secondarygradient: "n200A",
    secondaryinverse: "n200A",
    secondarysuccess: "g100",
    secondarywarning: "y100",
    secondarydanger: "r100",
};

export const backgroundHoverStyles: { [key in ButtonStyles]?: string } = {
    secondarydefault: "n300A",
    secondarygradient: "n300A",
    secondaryaccent: "b200",
    secondarysuccess: "g200",
    secondarywarning: "y200",
    secondarydanger: "r200",
    minimaldefault: "n100A",
    minimalgradient: "n100A",
    minimalaccent: "b100",
    minimalsuccess: "g100",
    minimalwarning: "y100",
    minimaldanger: "r100",
};

// exception for Gradients will be made else where
export const fontColorMapping: { [key: string]: string } = {
    secondarydefault: "n800A",
    secondaryaccent: "b400",
    secondarysuccess: "g500",
    secondarywarning: "y500",
    secondarydanger: "r500",
    tertiarydefault: "n800A",
    tertiaryaccent: "b400",
    tertiarysuccess: "g500",
    tertiarywarning: "y500",
    tertiarydanger: "r500",
    minimaldefault: "n800A",
    minimalaccent: "b400",
    minimalsuccess: "g500",
    minimalwarning: "y500",
    minimaldanger: "r500",
};
